import React from 'react';
import { Progress } from '../ui/progress';
import { UsageDetails } from '../../types/subscription';
import { safeMultiply, safeDivide, toSafeNumber } from '../../utils/number';

interface UsageProgressCardProps {
  usage: UsageDetails;
  variant: 'detailed' | 'compact';
  theme?: 'orange' | 'red' | 'blue' | 'dark';
  showStores?: boolean;
}

export const UsageProgressCard: React.FC<UsageProgressCardProps> = ({
  usage,
  variant = 'detailed',
  theme = 'dark',
  showStores = false
}) => {
  const getContainerClasses = () => {
    const baseClasses = 'space-y-4 backdrop-blur-sm rounded-xl p-6';
    
    switch (theme) {
      case 'orange':
        return `${baseClasses} bg-white/80 border border-orange-200`;
      case 'red':
        return `${baseClasses} bg-white/20`;
      case 'blue':
        return `${baseClasses} bg-blue-50/80 border border-blue-200`;
      default:
        return `${baseClasses} bg-white/20`;
    }
  };

  const getTitleClasses = () => {
    switch (theme) {
      case 'orange':
        return 'text-orange-800 font-semibold text-lg mb-4';
      case 'red':
        return 'text-white font-semibold text-lg mb-4';
      case 'blue':
        return 'text-blue-800 font-semibold text-lg mb-4';
      default:
        return 'text-white font-semibold text-lg mb-4';
    }
  };

  const getLabelClasses = () => {
    switch (theme) {
      case 'orange':
        return 'text-orange-700 font-medium';
      case 'red':
        return 'text-white font-medium';
      case 'blue':
        return 'text-blue-700 font-medium';
      default:
        return 'text-white font-medium';
    }
  };

  const getPercentageClasses = () => {
    switch (theme) {
      case 'orange':
        return 'text-orange-600 text-sm text-center';
      case 'red':
        return 'text-white/80 text-sm text-center';
      case 'blue':
        return 'text-blue-600 text-sm text-center';
      default:
        return 'text-white/80 text-sm text-center';
    }
  };

  const formatUsage = (current: number, limit: number) => {
    return `${current.toLocaleString()} / ${limit.toLocaleString()}`;
  };

  const calculatePercentage = (current: number, limit: number) => {
    return Math.round((current / limit) * 100);
  };

  const renderUsageItem = (
    label: string,
    current: number,
    limit: number,
    isLast: boolean = false
  ) => (
    <div className={`space-y-2 ${!isLast && variant === 'detailed' ? 'mb-4' : ''}`}>
      <div className={`flex justify-between ${getLabelClasses()}`}>
        <span>{label}</span>
        <span>{formatUsage(current, limit)}</span>
      </div>
      <Progress 
        value={(current / limit) * 100} 
        className="h-3 bg-white/20" 
      />
      <div className={getPercentageClasses()}>
        {calculatePercentage(current, limit)}% used
      </div>
    </div>
  );

  if (variant === 'compact') {
    // Show only the highest usage item
    const creditsPercentage = safeMultiply(safeDivide(toSafeNumber(usage.credits.current), toSafeNumber(usage.credits.limit)), 100);
    const syncsPercentage = safeMultiply(safeDivide(toSafeNumber(usage.syncs.current_month), toSafeNumber(usage.syncs.limit)), 100);
    
    if (creditsPercentage >= syncsPercentage) {
      return (
        <div className={getContainerClasses()}>
          {renderUsageItem(
            'Credits Used',
            usage.credits.current,
            usage.credits.limit,
            true
          )}
        </div>
      );
    } else {
      return (
        <div className={getContainerClasses()}>
          {renderUsageItem(
            'Monthly Syncs',
            usage.syncs.current_month,
            usage.syncs.limit,
            true
          )}
        </div>
      );
    }
  }

  // Detailed view
  return (
    <div className={getContainerClasses()}>
      <div className="text-center">
        <div className={getTitleClasses()}>
          Current Usage
        </div>
        
        {/* Credits Usage */}
        {renderUsageItem(
          'Credits Used',
          usage.credits.current,
          usage.credits.limit,
          false
        )}

        {/* Syncs Usage */}
        {renderUsageItem(
          'Monthly Syncs',
          usage.syncs.current_month,
          usage.syncs.limit,
          !showStores
        )}

        {/* Stores Usage (optional) */}
        {showStores && renderUsageItem(
          'Stores',
          usage.stores.current,
          usage.stores.limit,
          true
        )}
      </div>
    </div>
  );
};